import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Plus, CheckCircle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useTransactions } from "@/hooks/useTransactions";
import { useTransaction } from "@/hooks/useTransaction";
import { toast } from "sonner";
import { formatCurrency } from "@/types/construction";

interface MilestoneScheduleData {
  workId: string;
  workName: string;
  totalValue: number;
  milestones: Array<{
    stageName: string;
    percentage: number;
    amount: number;
    dueDate: Date;
  }>;
}

export function MilestonesPage() {
  const { costCenters, isLoading: isLoadingCostCenters } = useCostCenters();
  const { transactions, isLoading: isLoadingTransactions } = useTransactions();
  const { create: createTransaction } = useTransaction();

  const [selectedWorkId, setSelectedWorkId] = useState<string>("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state for creating milestone schedule
  const [formData, setFormData] = useState<MilestoneScheduleData>({
    workId: "",
    workName: "",
    totalValue: 0,
    milestones: [
      { stageName: "Sinal (30%)", percentage: 30, amount: 0, dueDate: new Date() },
      { stageName: "Entrega Estrutura (30%)", percentage: 30, amount: 0, dueDate: new Date() },
      { stageName: "Conclusão (40%)", percentage: 40, amount: 0, dueDate: new Date() },
    ],
  });

  const activeCostCenters = costCenters?.filter((cc) => cc.is_active) || [];

  // Filter transactions that are installment-based income for construction works
  // These represent milestone payments
  const milestoneTransactions = transactions?.filter(
    (t) =>
      t.type === "income" &&
      t.total_installments &&
      t.total_installments > 1 &&
      t.cost_center_id
  ) || [];

  // Group transactions by cost center to show milestone schedules
  const milestoneSchedules = new Map<string, any[]>();
  milestoneTransactions.forEach((t) => {
    if (!t.cost_center_id) return;
    const existing = milestoneSchedules.get(t.cost_center_id) || [];
    existing.push(t);
    milestoneSchedules.set(t.cost_center_id, existing);
  });

  useEffect(() => {
    if (formData.totalValue > 0) {
      // Recalculate milestone amounts when total value changes
      setFormData((prev) => ({
        ...prev,
        milestones: prev.milestones.map((m) => ({
          ...m,
          amount: (prev.totalValue * m.percentage) / 100,
        })),
      }));
    }
  }, [formData.totalValue]);

  const handleWorkSelect = (workId: string) => {
    const work = activeCostCenters.find((cc) => cc.id === workId);
    if (work) {
      setFormData((prev) => ({
        ...prev,
        workId,
        workName: work.name,
      }));
    }
  };

  const handleCreateMilestones = async () => {
    // Validation
    if (!formData.workId) {
      toast.error("Selecione uma obra");
      return;
    }

    if (formData.totalValue <= 0) {
      toast.error("Valor total deve ser maior que zero");
      return;
    }

    const totalPercentage = formData.milestones.reduce((sum, m) => sum + m.percentage, 0);
    if (totalPercentage !== 100) {
      toast.error(`Soma das porcentagens deve ser 100% (atual: ${totalPercentage}%)`);
      return;
    }

    try {
      // Create one transaction with installments to represent the milestone schedule
      // Description will include milestone stage info
      // Each installment represents one milestone payment

      await createTransaction({
        type: "income",
        description: `Obra: ${formData.workName} - Pagamento por Medições`,
        amount: formData.totalValue,
        due_date: format(formData.milestones[0].dueDate, "yyyy-MM-dd"),
        is_paid: false,
        cost_center_id: formData.workId,
        total_installments: formData.milestones.length,
        current_installment: 1,
        notes: formData.milestones
          .map((m, i) => `${i + 1}. ${m.stageName}: ${m.percentage}% - ${formatCurrency(m.amount)}`)
          .join("\n"),
      });

      toast.success("Cronograma de medições criado com sucesso!");
      setShowCreateForm(false);

      // Reset form
      setFormData({
        workId: "",
        workName: "",
        totalValue: 0,
        milestones: [
          { stageName: "Sinal (30%)", percentage: 30, amount: 0, dueDate: new Date() },
          { stageName: "Entrega Estrutura (30%)", percentage: 30, amount: 0, dueDate: new Date() },
          { stageName: "Conclusão (40%)", percentage: 40, amount: 0, dueDate: new Date() },
        ],
      });
    } catch (error) {
      console.error("Error creating milestone schedule:", error);
      toast.error("Erro ao criar cronograma de medições");
    }
  };

  const isLoading = isLoadingCostCenters || isLoadingTransactions;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando medições...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Medições (Recebimentos)</h1>
          <p className="text-muted-foreground">
            Gerencie pagamentos por etapas das suas obras
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="mr-2 h-4 w-4" />
          {showCreateForm ? "Cancelar" : "Novo Cronograma"}
        </Button>
      </div>

      {/* Create Milestone Schedule Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Criar Cronograma de Medições</CardTitle>
            <CardDescription>
              Defina as etapas de pagamento para uma obra
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Work Selection */}
            <div className="space-y-2">
              <Label>Obra</Label>
              <Select value={formData.workId} onValueChange={handleWorkSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a obra" />
                </SelectTrigger>
                <SelectContent>
                  {activeCostCenters.map((cc) => (
                    <SelectItem key={cc.id} value={cc.id}>
                      {cc.code && `${cc.code.toString().padStart(4, "0")} - `}
                      {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Total Value */}
            <div className="space-y-2">
              <Label>Valor Total da Obra</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.totalValue || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    totalValue: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>

            {/* Milestones */}
            <div className="space-y-2">
              <Label>Etapas de Pagamento</Label>
              <div className="border rounded-lg p-4 space-y-4">
                {formData.milestones.map((milestone, index) => (
                  <div key={index} className="grid grid-cols-4 gap-4 items-end">
                    <div className="col-span-2 space-y-2">
                      <Label className="text-xs">Nome da Etapa</Label>
                      <Input
                        placeholder="Ex: Sinal, Entrega Estrutura"
                        value={milestone.stageName}
                        onChange={(e) => {
                          const newMilestones = [...formData.milestones];
                          newMilestones[index].stageName = e.target.value;
                          setFormData((prev) => ({ ...prev, milestones: newMilestones }));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Porcentagem (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={milestone.percentage}
                        onChange={(e) => {
                          const newMilestones = [...formData.milestones];
                          const percentage = parseInt(e.target.value) || 0;
                          newMilestones[index].percentage = percentage;
                          newMilestones[index].amount =
                            (formData.totalValue * percentage) / 100;
                          setFormData((prev) => ({ ...prev, milestones: newMilestones }));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Valor</Label>
                      <div className="text-sm font-medium bg-muted px-3 py-2 rounded-md">
                        {formatCurrency(milestone.amount)}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>Total:</span>
                    <span>
                      {formData.milestones.reduce((sum, m) => sum + m.percentage, 0)}%{" "}
                      - {formatCurrency(formData.totalValue)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleCreateMilestones} className="w-full">
              Criar Cronograma
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Existing Milestone Schedules */}
      <Card>
        <CardHeader>
          <CardTitle>Obras com Medições Ativas</CardTitle>
          <CardDescription>
            Acompanhe o status dos pagamentos por etapas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {milestoneSchedules.size === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum cronograma de medições cadastrado
            </div>
          ) : (
            <div className="space-y-6">
              {Array.from(milestoneSchedules.entries()).map(([costCenterId, txs]) => {
                const work = costCenters?.find((cc) => cc.id === costCenterId);
                if (!work) return null;

                // Assuming one transaction per milestone schedule
                // In MVP, we use installments to track milestones
                const schedule = txs[0];

                return (
                  <div key={costCenterId} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{work.name}</h3>
                        {work.code && (
                          <p className="text-sm text-muted-foreground">
                            Código: {work.code.toString().padStart(4, "0")}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(schedule.amount)}</div>
                        <div className="text-sm text-muted-foreground">
                          {schedule.current_installment} de {schedule.total_installments} medições
                        </div>
                      </div>
                    </div>

                    {/* Show notes as milestone breakdown */}
                    {schedule.notes && (
                      <div className="mt-4 text-sm space-y-1">
                        <div className="font-medium text-muted-foreground mb-2">Etapas:</div>
                        {schedule.notes.split("\n").map((line, i) => (
                          <div key={i} className="flex items-center gap-2">
                            {schedule.is_paid && schedule.current_installment >= i + 1 ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span>{line}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4">
                      <Badge variant={schedule.is_paid ? "default" : "secondary"}>
                        {schedule.is_paid ? "Pago" : "Aguardando Pagamento"}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
